/**
 * Data layer for `cap-periods-hourly-daily-monthly-lifetime`. Thin typed fetch wrappers over /api/v1/cap-periods-hourly-daily-monthly-lifetime.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CapPeriodsHourlyDailyMonthlyLifetimeRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCapPeriodsHourlyDailyMonthlyLifetimeResponse {
	count: number;
	items: CapPeriodsHourlyDailyMonthlyLifetimeRow[];
}

export interface CreateCapPeriodsHourlyDailyMonthlyLifetimeBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/cap-periods-hourly-daily-monthly-lifetime";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCapPeriodsHourlyDailyMonthlyLifetimeResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCapPeriodsHourlyDailyMonthlyLifetimeResponse>(res);
}

export async function create(body: CreateCapPeriodsHourlyDailyMonthlyLifetimeBody): Promise<CapPeriodsHourlyDailyMonthlyLifetimeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CapPeriodsHourlyDailyMonthlyLifetimeRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const capPeriodsHourlyDailyMonthlyLifetimeQueryKeys = {
	all: ["cap-periods-hourly-daily-monthly-lifetime"] as const,
	list: () => [...capPeriodsHourlyDailyMonthlyLifetimeQueryKeys.all] as const,
	detail: (id: string) => [...capPeriodsHourlyDailyMonthlyLifetimeQueryKeys.all, id] as const,
};
